import React from "react";
import {Typography} from "@mui/material";

import {Grey6, Grey17} from "../../theme/styles";

// How one approval slot reads before and after it is signed.
//
// WHY THIS IS SHARED RATHER THAN WRITTEN TWICE. Both the queue and the record's Approval tab drew
// their own version of "who has to sign and did they", and both leaned on `Meaning` alone. Meaning
// is what a signature in that slot WOULD mean -- it is the value chosen in the matrix editor, from
// Approved, Reviewed, Authorised -- and the noun and the past-tense verb are the same string. So an
// UNSIGNED slot rendered as "Management Review Board · Approved", which reads as a statement that
// they approved it. Reported by Ed on SOP-00124, where nobody had signed anything and stage 2 had
// not opened: an audit trail appearing to show an approval nobody gave.
//
// SAY IT IN WORDS, NOT IN COLOUR AND A GLYPH. The old row distinguished the two states by a ○/✓ and
// a grey/green, both of which vanish at a glance, in a printout, and for a reader who cannot
// separate the colours. The verb tense now does the work: "to approve" is an outstanding
// obligation, "approved by Dana Okafor" is a fact. Either could be read aloud and be unambiguous.

const SIGNED = "#067647";

// A LOOKUP, NOT STRING SURGERY. The first version stripped letters off the end -- "Approved" minus
// one character is "approve", so it looked right -- and produced "to reviewe", because English forms
// these two participles differently (approve+d, review+ed). There is no rule to derive here: the
// matrix offers a FIXED list (MEANINGS in ApprovalCellEditor, plus the retired "Authored"), so the
// phrasing is written down per meaning and anything unrecognised falls back to naming it verbatim.
const OBLIGATION: Record<string, string> = {
    reviewed: "to review",
    approved: "to approve",
    authored: "to attest authorship",   // retired, but old requests still carry it
};

/** "Approved" -> "to approve": what this slot is still WAITING for. */
export function asObligation(meaning: string | null): string {
    const word = (meaning ?? "").trim();
    if (!word) return "to sign";
    return OBLIGATION[word.toLowerCase()] ?? `to sign as ${word}`;
}

// EVERY FIELD OPTIONAL, because both callers hold rows typed `Record<string, any>` straight off the
// wire. Demanding USID here would be a type error at the call site that says nothing about the data.
export type SlotRow = {
    USID?: string;
    Stage?: number | null;
    StageLabel?: string | null;
    PersonaName?: string | null;
    Meaning?: string | null;
    Signature?: {PrintedName?: string | null; SignedDatetime?: string | null;
                 Decision?: string | null} | null;
};

/**
 * One line describing a slot.
 *
 * @param currentStage the request's open stage, so a slot waiting its turn can say so. A reader
 *                     otherwise has no way to tell "nobody has signed yet" from "nobody can".
 */
export default function SlotState({slot, currentStage}: {slot: SlotRow; currentStage?: number | null}) {
    const signature = slot.Signature;
    // The signature carries REJECT; the request's own status carries REJECTED. startsWith covers
    // both rather than betting on which one reached this row.
    const rejected = !!signature && String(signature.Decision ?? "").toUpperCase().startsWith("REJECT");

    if (signature) {
        // A REJECTION IS NOT AN APPROVAL and must never borrow its wording. Rejecting is signing --
        // the slot is filled -- so a row that only said "signed" would read as assent.
        return (
            <Typography style={{fontSize: 12, color: rejected ? "#B42318" : SIGNED}}>
                ✓ {slot.PersonaName}
                {" — "}
                {rejected ? "rejected" : (slot.Meaning ?? "signed").toLowerCase()}
                {signature.PrintedName ? ` by ${signature.PrintedName}` : ""}
                {signature.SignedDatetime
                    ? `, ${new Date(signature.SignedDatetime).toLocaleDateString()}` : ""}
            </Typography>
        );
    }

    // NOT OPEN YET is a different fact from NOT DONE YET, and only the first excuses the delay.
    const waiting = typeof slot.Stage === "number" && typeof currentStage === "number"
        && slot.Stage > currentStage;

    return (
        <Typography style={{fontSize: 12, color: Grey6}}>
            ○ {slot.PersonaName}
            {" — "}
            <span style={{color: Grey17}}>{asObligation(slot.Meaning ?? null)}</span>
            {waiting
                ? ` (stage ${slot.Stage}${slot.StageLabel ? ` · ${slot.StageLabel}` : ""}, not open yet)`
                : " (awaiting signature)"}
        </Typography>
    );
}
